// @ts-check
/**
 * services/online-status.js — reactive navigator.onLine + offline banner
 * driver (#189).
 *
 * navigator.onLine is famously unreliable on its own — Chrome reports
 * onLine=true whenever there's ANY network interface, even captive-
 * portal Wi-Fi that can't reach our backend. We only flip $isOnline to
 * false on confirmed offline transitions (the `offline` event), and we
 * pair that with optional /ping verification when the boot flow needs
 * a quick health check.
 *
 * The banner uses this atom; nothing else should poll navigator.onLine
 * directly.
 *
 * #194 phase C2 — first file in the codebase to opt into TypeScript
 * checking via the `// @ts-check` pragma at the top + JSDoc annotations
 * on the exports. tsc --noEmit (run via `npm run typecheck:inbox`)
 * verifies the call shapes against ../types/inbox.d.ts. Zero runtime
 * change — the file is still pure ES module loaded as raw .js.
 */

import { atom } from 'nanostores';

/** @type {import('nanostores').WritableAtom<boolean>} */
export const $isOnline = atom(typeof navigator === 'undefined' ? true : !!navigator.onLine);

let _wired = false;

/**
 * Wire the global online/offline event listeners. Idempotent — safe to
 * call multiple times; subsequent calls are no-ops.
 *
 * @returns {void}
 */
export function initOnlineStatus() {
  if (_wired || typeof window === 'undefined') return;
  _wired = true;

  window.addEventListener('online', () => {
    console.log('[v2 online-status] online');
    $isOnline.set(true);
  });
  window.addEventListener('offline', () => {
    console.log('[v2 online-status] offline');
    $isOnline.set(false);
  });
}
